"use strict";
/**
 * Market Navigation
 *
 * Handles all market-switching and navigation controls on the order page:
 * - Primary market list (a tap selects a different market within the event)
 * - Prev / Next navigation buttons
 * - Back-to-markets button
 * - Settings button (opens the settings modal)
 * - Enable/disable all order buttons toggle
 * - Two-click confirmation toggle
 * - Delayed price toggle (T0 / Delayed)
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.setupMarketNavigation = setupMarketNavigation;
const state_1 = require("../../state");
const ui_1 = require("../../ui");
const navigation_service_1 = require("../../services/navigation-service");
const market_filter_1 = require("../../markets/market-filter");
const market_switcher_1 = require("../../ui/market-switcher");
const primary_market_filter_1 = require("../../services/primary-market-filter");
const market_selection_service_1 = require("../../services/market-selection-service");
// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------
/**
 * Wire up all market-navigation and page-level controls.
 *
 * @param params.allButtons      - Standard Buy/Sell buttons for enable/disable toggle
 * @param params.baseballButtons - Baseball mode buttons for enable/disable toggle
 * @param params.armTimeoutId    - Shared arm-timeout ref (cleared when toggling confirm)
 * @param params.confirmRequired - Mutable ref to the confirm-required flag
 * @param params.updateConfirmToggle   - Callback to refresh the 2C toggle button label
 * @param params.updateDelayedPriceToggle - Callback to refresh the Dly/T0 button label
 */
function setupMarketNavigation(params) {
    const { allButtons, baseballButtons, armTimeoutId, confirmRequired, updateConfirmToggle, updateDelayedPriceToggle, } = params;
    // --- Back button ---
    const backBtn = document.getElementById("back-btn");
    if (backBtn) {
        backBtn.addEventListener("click", () => (0, navigation_service_1.navigateToEvents)());
    }
    // --- Settings button ---
    const settingsBtn = document.getElementById("settings-btn-order");
    if (settingsBtn) {
        settingsBtn.addEventListener("click", () => {
            const state = (0, state_1.getState)();
            (0, ui_1.populateSettingsForm)(state.tradingSettings);
            (0, ui_1.showSettingsModal)();
        });
    }
    // --- Primary market list: a tap switches to that market ---
    document.getElementById("market-switcher")?.addEventListener("click", (e) => {
        const row = e.target.closest(`[${market_switcher_1.MARKET_ROW_ATTR}]`);
        const newMarketId = row?.getAttribute(market_switcher_1.MARKET_ROW_ATTR);
        // The current market: nothing to switch (and no polling restart).
        if (newMarketId && newMarketId !== (0, state_1.getState)().selectedMarket?.id)
            (0, market_selection_service_1.switchMarket)(newMarketId);
    });
    // --- Prev / Next market navigation ---
    const marketPrevBtn = document.getElementById("market-prev-btn");
    const marketNextBtn = document.getElementById("market-next-btn");
    const navigateMarket = (direction) => {
        const state = (0, state_1.getState)();
        const markets = state.availableMarkets;
        const selected = state.selectedMarket;
        if (!markets || !selected)
            return;
        // Within the picker's filter (markets/market-filter.ts).
        const newId = (0, market_filter_1.adjacentFilteredMarketId)(markets, (0, primary_market_filter_1.getPrimaryFilter)(), selected.id, direction);
        if (newId)
            (0, market_selection_service_1.switchMarket)(newId);
    };
    marketPrevBtn?.addEventListener("click", () => navigateMarket("prev"));
    marketNextBtn?.addEventListener("click", () => navigateMarket("next"));
    // --- Enable / disable all order buttons ---
    const enableButtonsBtn = document.getElementById("enable-buttons-btn");
    enableButtonsBtn?.addEventListener("click", () => {
        allButtons.forEach((btn) => {
            btn?.toggleAttribute("disabled");
            btn?.classList.toggle("opacity-50");
            btn?.classList.remove("confirm-pending");
        });
        baseballButtons.forEach((btn) => {
            btn?.toggleAttribute("disabled");
            btn?.classList.toggle("opacity-50");
            btn?.classList.remove("confirm-pending");
        });
        if (armTimeoutId.value) {
            clearTimeout(armTimeoutId.value);
            armTimeoutId.value = null;
        }
    });
    // --- Two-click confirmation toggle ---
    const confirmToggleBtn = document.getElementById("toggle-confirm-btn");
    confirmToggleBtn?.addEventListener("click", () => {
        confirmRequired.value = !confirmRequired.value;
        updateConfirmToggle();
        allButtons.forEach((btn) => btn?.classList.remove("confirm-pending"));
        baseballButtons.forEach((btn) => btn?.classList.remove("confirm-pending"));
        if (armTimeoutId.value) {
            clearTimeout(armTimeoutId.value);
            armTimeoutId.value = null;
        }
    });
    // --- Delayed price toggle (T0 / Delayed) ---
    const delayedPriceToggleBtn = document.getElementById("toggle-delayed-price-btn");
    delayedPriceToggleBtn?.addEventListener("click", () => {
        const state = (0, state_1.getState)();
        const newValue = !state.tradingSettings.useDelayedPrice;
        (0, state_1.updateState)({
            tradingSettings: {
                ...state.tradingSettings,
                useDelayedPrice: newValue,
            },
        });
        (0, state_1.saveSettingsToStorage)();
        updateDelayedPriceToggle();
    });
    // Initialise toggle labels
    updateConfirmToggle();
    updateDelayedPriceToggle();
}
